'use client';

import { Box, Typography, useTheme } from '@mui/material';
import { Fragment, useEffect, useState } from 'react';
import Marquee from 'react-fast-marquee';

import { ClientOnly } from '@/components/ClientOnlyMarquee';

export interface MarqueeBulletsProps {
  items: string[];
  label?: string;
  /** Wrap in a Box with mt spacing, like the HomepageHero usage */
  withTopMargin?: boolean;
}

export default function MarqueeBullets({
  items,
  label = 'Lend is:',
  withTopMargin = false,
}: MarqueeBulletsProps) {
  const theme = useTheme();
  const [isClient, setIsClient] = useState(false);

  useEffect(() => setIsClient(true), []);

  if (!items || items.length === 0) return null;

  const itemList = (trailingMargin?: boolean) =>
    items.map((item, index) => (
      <Fragment key={`marquee-item-${index}`}>
        <Typography
          color="white"
          display="inline-block"
          sx={{
            fontSize: 22,
            mr: trailingMargin && index + 1 === items.length ? 10 : 0,
          }}
        >
          {item}
        </Typography>
        {index + 1 !== items.length && (
          <Typography
            color="white"
            display="inline"
            sx={{
              px: 2,
              color: theme.palette['purple'][600],
              fontSize: 22,
            }}
          >
            /
          </Typography>
        )}
      </Fragment>
    ));

  const labelEl = (
    <Typography
      variant="pill"
      display="inline-block"
      sx={{ px: 2, fontSize: 22, mx: 2, mt: '0 !important' }}
    >
      <strong>{label}</strong>
    </Typography>
  );

  const staticFallback = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      {labelEl}
      {itemList()}
    </Box>
  );

  const inner = isClient ? (
    <ClientOnly fallback={staticFallback}>
      <Marquee>
        {labelEl}
        {itemList(true)}
      </Marquee>
    </ClientOnly>
  ) : (
    staticFallback
  );

  return (
    <Box
      sx={{ display: 'block', verticalAlign: 'middle', ...(withTopMargin ? { mt: 9 } : {}) }}
      suppressHydrationWarning
    >
      {inner}
    </Box>
  );
}
